import { createEffect, createResource } from "solid-js"
import { createStore } from "solid-js/store"
import { SettingsListV2 } from "@/components/settings-v2/parts/list"
import { SettingsRowV2 } from "@/components/settings-v2/parts/row"
import { useLanguage } from "@/context/language"
import { getWorkspaceProfile, updateWorkspaceProfile, updateWorkspaceSettings } from "@/cowork/api"
import { rememberWorkspaceSlug } from "@/cowork/workspace-route"

const inputClass =
  "h-9 min-w-0 flex-1 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-v2-text-text-base outline-none sm:w-64 sm:flex-none"
const buttonClass =
  "h-9 rounded-md bg-v2-background-bg-contrast px-3 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"

function useHandleError() {
  const language = useLanguage()
  return (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error)
    if (message.endsWith("_taken")) return language.t("cowork.onboarding.handle.taken")
    if (message.endsWith("_reserved")) return language.t("cowork.onboarding.handle.reserved")
    if (message.endsWith("_invalid")) return language.t("cowork.onboarding.handle.invalid")
    return message
  }
}

export function CoworkWorkspaceUrlRow(props: { slug?: string; loading: boolean; onError: (message: string) => void }) {
  const language = useLanguage()
  const handleError = useHandleError()
  const [state, setState] = createStore({ slug: "", saving: false })
  createEffect(() => {
    if (props.slug) setState("slug", props.slug)
  })

  const save = async (event: SubmitEvent) => {
    event.preventDefault()
    const slug = state.slug.trim().toLowerCase()
    if (!slug || state.saving || slug === props.slug) return
    setState("saving", true)
    props.onError("")
    try {
      const saved = await updateWorkspaceSettings({ slug })
      rememberWorkspaceSlug(saved.slug || slug)
      window.location.assign(`/${saved.slug || slug}/settings/workspace`)
    } catch (error) {
      setState("saving", false)
      props.onError(handleError(error))
    }
  }

  return (
    <SettingsRowV2
      title={language.t("cowork.enterprise.settings.workspaceUrl")}
      description={language.t("cowork.enterprise.settings.workspaceUrlDescription")}
    >
      <form class="flex w-full items-center justify-end gap-2" onSubmit={save}>
        <span class="text-[12px] text-v2-text-text-muted">{`${window.location.host}/`}</span>
        <input
          class={inputClass}
          value={state.slug}
          spellcheck={false}
          aria-label={language.t("cowork.enterprise.settings.workspaceUrl")}
          disabled={state.saving || props.loading}
          onInput={(event) => setState("slug", event.currentTarget.value.toLowerCase())}
        />
        <button
          type="submit"
          class={buttonClass}
          disabled={!state.slug.trim() || state.saving || state.slug.trim() === props.slug}
        >
          {state.saving
            ? language.t("cowork.enterprise.settings.saving")
            : language.t("cowork.enterprise.settings.save")}
        </button>
      </form>
    </SettingsRowV2>
  )
}

export function CoworkWorkspaceProfileSection(props: { onError: (message: string) => void; onSaved: () => void }) {
  const language = useLanguage()
  const handleError = useHandleError()
  const [profile, profileActions] = createResource(getWorkspaceProfile)
  const [state, setState] = createStore({ username: "", saving: false })
  createEffect(() => {
    const username = profile()?.username
    if (username) setState("username", username)
  })

  const save = async (event: SubmitEvent) => {
    event.preventDefault()
    const username = state.username.trim().toLowerCase()
    if (!username || state.saving) return
    setState("saving", true)
    props.onError("")
    try {
      await updateWorkspaceProfile({ username })
      await profileActions.refetch()
      props.onSaved()
    } catch (error) {
      props.onError(handleError(error))
    } finally {
      setState("saving", false)
    }
  }

  return (
    <section>
      <h2 class="settings-v2-section-title">{language.t("cowork.enterprise.settings.profile")}</h2>
      <SettingsListV2>
        <SettingsRowV2
          title={language.t("cowork.enterprise.settings.username")}
          description={language.t("cowork.enterprise.settings.usernameDescription")}
        >
          <form class="flex w-full items-center justify-end gap-2" onSubmit={save}>
            <span class="text-[12px] text-v2-text-text-muted">@</span>
            <input
              class={inputClass}
              value={state.username}
              spellcheck={false}
              aria-label={language.t("cowork.enterprise.settings.username")}
              disabled={state.saving || profile.loading}
              onInput={(event) => setState("username", event.currentTarget.value.toLowerCase())}
            />
            <button
              type="submit"
              class={buttonClass}
              disabled={!state.username.trim() || state.saving || state.username.trim() === profile()?.username}
            >
              {state.saving
                ? language.t("cowork.enterprise.settings.saving")
                : language.t("cowork.enterprise.settings.save")}
            </button>
          </form>
        </SettingsRowV2>
      </SettingsListV2>
    </section>
  )
}
